<template>
  <section id="rechargeTransactionFilter">
    <transition name="fade" mode="out-in" appear>
      <VSpinner v-if="isLoading" class="offset-top-1" />
      <form v-else>
        <h3 class="offset-bottom-2">{{ $t('common.filterHeader') }}</h3>
        <div class="row offset-top-2">
          <div class="col-l-3 col-m-6 col-s-12" :title="$tt('common.filterFrom')">
            <label class="align-with-buttons">
              <span class="form-label">{{ $t('common.periodFrom') }}</span>
              <VDatepicker v-model="periodFrom" :disabled-date="disabledDate" input-class="period-from" />
            </label>
          </div>
          <div class="col-l-3 col-m-6 col-s-12">
            <label class="align-with-buttons" :title="$tt('common.filterTo')">
              <span class="form-label">{{ $t('common.periodUntil') }}</span>
              <VDatepicker v-model="periodUntil" :disabled-date="disabledDate" input-class="period-until" />
            </label>
          </div>
          <div class="col-l-6 col-m-12">
            <VButton
              label="common.btnSearchPeriod"
              class="btn-default pull-left offset-bottom-1"
              style="margin-right: 24px"
              :disabled="searchform.isSearchLoading"
              @click.prevent="findRange"
            />
            <div class="btn-sectioned pull-left">
              <VButton label="common.btnSearchToday" type="button" class="btn btn-default" :disabled="searchform.isSearchLoading" @click.prevent="findToday" />
              <VButton
                label="common.btnSearchYesterday"
                type="button"
                class="btn btn-default"
                :disabled="searchform.isSearchLoading"
                @click.prevent="findYesterday"
              />
              <VButton
                label="common.btnSearchLastWeek"
                type="button"
                class="btn btn-default"
                :disabled="searchform.isSearchLoading"
                @click.prevent="findLastWeek"
              />
            </div>
          </div>
        </div>

        <VSearchFormStatus v-bind="searchform" spinner closable @close="searchReset" />
      </form>
    </transition>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import isValid from 'date-fns/isValid';
import subDays from 'date-fns/subDays';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import VDatepicker from '@/components/Form/DatePicker';
import VSpinner from '@/components/Spinner';
import SearchFormMixin from '@/mixins/searchform';
import VButton from '@/components/Button';
import { dateUrlFormat, dateTimeTransferFormat } from '@/utils/date';
import Proxy from '@/proxies/RechargeTransactionsProxy';
import { RECHARGE_TRANSACTIONS, RECHARGE_TRANSACTIONS_OVERVIEW, RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS } from '@route-types';

const transactionState = Object.freeze({
  BOOKED: 20,
  CLEARED: 100,
  REVERSED: 200,
  FAILED: 300,
});

export default {
  name: 'RechargeTransactionsFilter',

  components: {
    VSpinner,
    VDatepicker,
    VButton,
    VSearchFormStatus: () => import('@/components/SearchFormStatus'),
  },

  mixins: [SearchFormMixin],

  data() {
    return {
      transactionState,
      isLoading: true,
      periodFrom: null,
      periodUntil: new Date(),
      searchableTimePeriod: null,
      maximumTimeFrame: null,
      timeFramePromise: new Proxy().timeFrame(),
    };
  },

  computed: {
    ...mapGetters('system', ['hasShowCCTransactionView']),

    routeParams() {
      return this.$route.params;
    },
  },

  watch: {
    routeParams: {
      handler: 'onParamsChanged',
      deep: true,
    },
    periodFrom: 'adjustPeriodUntil',
    periodUntil: 'adjustPeriodFrom',
  },

  async created() {
    const response = await this.timeFramePromise;
    this.searchableTimePeriod = response.searchableTimePeriod;
    this.maximumTimeFrame = response.maximumTimeFrame;
    this.isLoading = false;

    if (!this.parseDates(this.routeParams)) {
      this.adjustPeriodFrom();
      // use replace instead of push so browser back won't get stuck here
      if (this.$route.name !== RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS) {
        this.saveRangeRoute('replace');
      }
    } else {
      this.searchList();
    }
  },

  methods: {
    reset() {
      this.$router.push({ name: RECHARGE_TRANSACTIONS });
    },

    adjustPeriodUntil() {
      if (!this.isRangeValid()) {
        this.periodUntil = addDays(this.periodFrom, this.searchableTimePeriod - 1);
        this.$nextTick(() => {
          this.searchWarningMessage(this.exceedsMaxTime(this.searchableTimePeriod, 'Until'));
        });
      } else {
        this.searchReset();
      }
    },

    adjustPeriodFrom() {
      if (!this.isRangeValid()) {
        this.periodFrom = subDays(this.periodUntil, this.searchableTimePeriod - 1);
        this.$nextTick(() => {
          this.searchWarningMessage(this.exceedsMaxTime(this.searchableTimePeriod, 'From'));
        });
      } else {
        this.searchReset();
      }
    },

    async onParamsChanged(val, oldVal) {
      await this.timeFramePromise;
      if (!val.from) {
        this.periodFrom = new Date();
        this.periodUntil = new Date();
        this.saveRangeRoute('replace');
      } else if ((val && !oldVal) || val.from !== oldVal.from || val.to !== oldVal.to) {
        if (this.parseDates(val)) this.searchList();
      }
    },

    parseDates(range) {
      const periodFrom = parse(range.from, dateUrlFormat, new Date());
      const periodUntil = parse(range.to, dateUrlFormat, new Date());
      if (!isValid(periodFrom)) return false;
      if (!isValid(periodUntil)) return false;
      this.periodFrom = periodFrom;
      this.periodUntil = periodUntil;
      return this.isRangeValid();
    },

    isRangeValid() {
      if (this.periodFrom === null) {
        this.searchErrorMessage(this.$t('common.errInvalidPeriodFrom'));
        return false;
      }
      if (this.periodUntil === null) {
        this.searchErrorMessage(this.$t('common.errInvalidPeriodUntil'));
        return false;
      }

      if (this.periodFrom > this.periodUntil) {
        this.searchErrorMessage(this.$t('common.errPeriodFromBeforePeriodUntil'));
        return false;
      }

      const periodSearchDays = differenceInCalendarDays(this.periodUntil, this.periodFrom);
      if (periodSearchDays >= this.searchableTimePeriod) {
        return false;
      }
      return true;
    },

    findRange() {
      this.saveRangeRoute();
    },

    findToday() {
      if (format(this.periodFrom, dateUrlFormat) !== format(new Date(), dateUrlFormat)) {
        this.periodFrom = new Date();
      }
      if (format(this.periodUntil, dateUrlFormat) !== format(new Date(), dateUrlFormat)) {
        this.periodUntil = new Date();
      }
      this.saveRangeRoute();
    },

    findYesterday() {
      if (format(this.periodFrom, dateUrlFormat) !== format(subDays(new Date(), 1), dateUrlFormat)) {
        this.periodFrom = subDays(new Date(), 1);
      }
      if (format(this.periodUntil, dateUrlFormat) !== format(subDays(new Date(), 1), dateUrlFormat)) {
        this.periodUntil = subDays(new Date(), 1);
      }
      this.saveRangeRoute();
    },

    findLastWeek() {
      if (format(this.periodFrom, dateUrlFormat) !== format(subDays(new Date(), 7), dateUrlFormat)) {
        this.periodFrom = subDays(new Date(), 7);
      }
      if (format(this.periodUntil, dateUrlFormat) !== format(new Date(), dateUrlFormat)) {
        this.periodUntil = new Date();
      }
      this.saveRangeRoute();
    },

    exceedsMaxTime(timePeriod, adjustedField) {
      return this.$tc(`common.errReachedMaxSearchPeriod${adjustedField}`, timePeriod, {
        timePeriod,
      });
    },

    async searchList() {
      this.searchLoading();
      try {
        const requestPeriodFrom = this.periodFrom;
        const requestPeriodUntil = this.periodUntil;
        /* xtc will check if periodFrom is smaller then periodUntil
               periodFrom and periodUntil will then be used directly in the SQL Query
               to select data between periodFrom and periodUntil!
               So the date time has to be set correctly */
        requestPeriodFrom.setHours(0);
        requestPeriodFrom.setMinutes(0);
        requestPeriodFrom.setSeconds(0);
        requestPeriodFrom.setMilliseconds(0);

        requestPeriodUntil.setHours(23);
        requestPeriodUntil.setMinutes(59);
        requestPeriodUntil.setSeconds(59);
        requestPeriodUntil.setMilliseconds(999);

        let response = await new Proxy({
          periodFrom: format(requestPeriodFrom, dateTimeTransferFormat),
          periodUntil: format(requestPeriodUntil, dateTimeTransferFormat),
        }).all();

        /* Customers may only see booked or cleared transactions */
        if (!this.hasShowCCTransactionView)
          response = response.filter(row => [transactionState.BOOKED, transactionState.CLEARED].includes(row.transactionStateId));

        if (response.length === 0) {
          this.searchErrorNoData();
        } else {
          this.searchSuccess();
        }
        this.$emit('update', response);
      } catch (error) {
        this.$emit('update', null);
        this.searchErrorMessage(this.$t('common.errorDefault'));
      }
    },

    saveRangeRoute(method) {
      if (!method) method = 'push';
      const from = format(this.periodFrom, dateUrlFormat);
      const to = format(this.periodUntil, dateUrlFormat);
      this.$router[method]({ name: RECHARGE_TRANSACTIONS_OVERVIEW, params: { ...this.routeParams, from, to, disableScrolling: null } }).catch(() => {});
    },

    disabledDate(date) {
      const minDate = subDays(new Date(), this.maximumTimeFrame);
      minDate.setHours(0, 0, 0, 0);
      const maxDate = new Date();
      maxDate.setHours(23, 59, 59, 59);
      return minDate.getTime() >= date.getTime() || date.getTime() > maxDate.getTime();
    },
  },
};
</script>

<style scoped>
.align-with-buttons {
  position: relative;
  top: -22px;
  width: 100%;
}
</style>
